import events from '~/modules/game-events/game-events';
import { RpcServer } from '~/modules/network/rpc/rpc-server';
import { ServerSubscriptionRegistry } from '~/modules/network/rpc/server-subscription-registry';
import { ChannelName, SubscriptionChannels } from '~/modules/remote-mic/network/client/subscriptions';
import { NetworkMessages } from '~/modules/remote-mic/network/messages';
import { SenderInterface, ServerTransport } from '~/modules/remote-mic/network/server/transport/interface';
import { LanRelayServerTransport } from '~/modules/remote-mic/network/server/transport/lan-relay-server';
import { MicRelayServerTransport } from '~/modules/remote-mic/network/server/transport/mic-relay-server';
import { PartyKitServerTransport } from '~/modules/remote-mic/network/server/transport/party-kit-server';
import { WebSocketServerTransport } from '~/modules/remote-mic/network/server/transport/web-socket-server';
import RemoteMicManager from '~/modules/remote-mic/remote-mic-manager';
import generateRoomCode from '~/modules/utils/generate-room-code';
import storage from '~/modules/utils/storage';
import { RemoteMicConnectionTypeSetting } from '~/routes/settings/settings-state';

import { serverHandlers } from './server-handlers';

export const GAME_CODE_KEY = 'room_id_key';
export const GAME_CODE_LENGTH = 5;

export const storeGameCode = (gameCode: string) => {
  storage.session.setItem(GAME_CODE_KEY, gameCode);
};

export class NetworkServer {
  private gameCode = storage.session.getItem(GAME_CODE_KEY)!;
  private started = false;
  private transport: ServerTransport | undefined;

  private rpcServer = new RpcServer<typeof serverHandlers, SubscriptionChannels>(
    serverHandlers,
    (peerId) => RemoteMicManager.getPermission(peerId),
    (channel, message) => RemoteMicManager.broadcastToChannel(channel, message),
    (peerId) => this.transport?.removePlayer(peerId),
  );

  // Only the last-value cache half of the registry is used here: which mic is subscribed to what
  // lives in RemoteMicManager, which remembers it across a host reload and does the fan-out.
  private subscriptions = new ServerSubscriptionRegistry<SubscriptionChannels>();

  public constructor() {
    if (!this.gameCode) {
      // One character short of GAME_CODE_LENGTH: `getGameCode()` prepends the transport-type letter,
      // and that prefix has to fit within the length remote mics type in.
      this.gameCode = generateRoomCode(GAME_CODE_LENGTH - 1);
    }

    global?.addEventListener?.('beforeunload', () => {
      RemoteMicManager.getRemoteMics().forEach((remoteMic) => remoteMic.connection.close());
      this.transport?.disconnect();
    });
  }

  private handleTransportMessage = (event: NetworkMessages, sender: SenderInterface) => {
    const type = event.t;

    if (type === 'register') {
      RemoteMicManager.addRemoteMic(event.id, event.name, sender, event.silent, event.lag, event.resumed);
    } else if (type === 'unregister') {
      RemoteMicManager.removeRemoteMic(sender.peer);
    } else if (type === 'rpc-sub') {
      RemoteMicManager.addSubscription(sender.peer, event.channel);
      // If there is a cached value for this channel, push it immediately to the new subscriber
      // so they don't have to wait for the next change to receive the current state.
      const lastValue = this.subscriptions.getLastValue(event.channel);
      if (lastValue) {
        sender.send({
          t: 'rpc-pub',
          channel: event.channel,
          data: lastValue.data,
        } as NetworkMessages);
      }
    } else if (type === 'rpc-unsub') {
      RemoteMicManager.removeSubscription(sender.peer, event.channel);
    } else if (type === 'ping') {
      // A phone dropped for going silent still has its socket, and a pong would keep it sure it is
      // connected while nothing it sends is read. Left unanswered, its deadline makes it re-register.
      if (!RemoteMicManager.getRemoteMicById(sender.peer)) return;
      sender.send({ t: 'pong' } as NetworkMessages);
    } else if (type === 'pong') {
      RemoteMicManager.getRemoteMicById(sender.peer)?.onPong();
    } else if (type === 'rpc') {
      this.rpcServer.handleMessage(event, sender);
    }
  };

  public start = () => {
    if (!this.transport) {
      const type = RemoteMicConnectionTypeSetting.get();
      this.transport =
        type === 'WebSockets'
          ? new WebSocketServerTransport()
          : type === 'LanRelay'
            ? new LanRelayServerTransport()
            : type === 'MicRelay'
              ? new MicRelayServerTransport()
              : new PartyKitServerTransport();
      // Attached to the transport once, not once per connection. `connect()` runs again on every
      // reconnect, and a listener added inside it stayed for the life of the page: with two
      // listeners every pong armed two mic ping timers, then four, then eight, and about twenty
      // seconds after its first dropped socket the room drowned every phone in pings.
      this.transport.addListener(this.handleTransportMessage);
    }
    if (this.started) return;
    this.started = true;
    if (this.reconnectTimeout !== null) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    console.log('connection started', this.getGameCode());
    storeGameCode(this.gameCode);

    this.transport.connect(
      this.getGameCode(),
      () => {
        console.log('connected', this.getGameCode());
        this.reconnectAttempt = 0;

        events.micServerStarted.dispatch();
      },
      () => {
        events.micServerStopped.dispatch();
        this.started = false;

        this.scheduleReconnect();
      },
    );
  };

  private reconnectAttempt = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * The relay drops every phone in the room the moment this socket goes (close reason
   * `room-disconnected`), and refuses them again with `peer-unavailable` until the room is
   * re-registered - so the room is down for exactly as long as this takes. Hence the quick first
   * retry, backing off to five seconds if the network is properly gone, with jitter so a room full
   * of phones and the game itself don't all retry on the same tick.
   */
  private scheduleReconnect = () => {
    if (this.reconnectTimeout !== null || this.started) return;

    const delay = Math.min(250 * 2 ** this.reconnectAttempt, 5_000) + Math.random() * 250;
    this.reconnectAttempt++;

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.start();
    }, delay);
  };

  public isStarted = () => this.started;

  public getLatency = () => this.transport?.getCurrentPing() ?? 0;

  // Publish data to all clients subscribed to a named channel
  public publish = <C extends ChannelName>(channel: C, data: SubscriptionChannels[C]): void => {
    this.subscriptions.setLastValue(channel, data);
    this.rpcServer.publish(channel, data);
  };

  // Send a server-initiated call to every currently connected client
  public callAllClients = (method: string, ...args: unknown[]): void => {
    this.rpcServer.broadcastClientCall(RemoteMicManager.getRemoteMics(), method, ...args);
  };

  // Send a server-initiated call to a single connected client
  public callClient = (micId: string, method: string, ...args: unknown[]): void => {
    const mic = RemoteMicManager.getRemoteMicById(micId);
    if (mic) {
      this.rpcServer.callClient(mic.connection, method, ...args);
    }
  };

  /**
   * The first character says which relay to meet on, so a phone can dial the right one from the
   * code alone. A phone from a build that has never heard of the letter fails to find the room and
   * says so, which is the right answer - it cannot reach this game.
   */
  public getGameCode = (): string => {
    const type = RemoteMicConnectionTypeSetting.get();
    const prefix = type === 'WebSockets' ? 'w' : type === 'MicRelay' ? 'm' : type === 'LanRelay' ? 'l' : 'k';

    return prefix + this.gameCode;
  };
}
